import {
	alphabeticalSort,
	type AnsweredQuestion,
	type FieldOption,
	isSome,
	omit,
} from "@sveabank/cc-common";
import { v4 as getUuid } from "uuid";
import { useApplicationId } from "../../hooks/useApplicationId";
import {
	useConfigureClcCardMutation,
	useConfirmClcProductConfigMutation,
	useFetchClcAmlQuestionsQuery,
	useFetchClcApplicationDetailsQuery,
	useFetchClcApplicationQuestionsQuery,
	useFetchClcApplicationsQuery,
	useFetchClcBeneficialOwnerQuestionsQuery,
	useFetchClcCardsQuery,
	useFetchClcControlTypesQuery,
	useFetchClcCountriesQuery,
	useFetchClcRolesQuery,
	useFetchClcSelectedPackageQuery,
	useFetchClcSignatoryCombinationsQuery,
	useFetchClcSniCodesQuery,
	usePostClcAmlAnswersMutation,
	usePostClcApplicationQuestionsMutation,
	usePostClcBeneficialOwnerAnswersMutation,
	usePostClcBeneficialOwnershipMutation,
	usePostClcCompanyRegistrationMutation,
	usePostClcCompanySharesMutation,
	usePostClcRepresentativesMutation,
	useRemoveClcAddonsMutation,
	useSelectClcAddonMutation,
	useSetupClcAgreementMutation,
} from "../../services/clc-application";
import type {
	ClcBeneficialOwnerAnswerPostReq,
	ClcBeneficialOwnership,
	ClcCompanyRegistrationForm,
	ClcCompanyShares,
	ClcCompanySharesForm,
	ClcCompanyStructureInput,
	ClcDebitCardConfigForm,
} from "../../types/clc-application";
import { getApplicationLanguage } from "../../utils/storage";
import { convertCountriesToFormValues } from "../contact-info-page/helpers";

export const useClcCompanyApplication = () => {
	const applicationId = useApplicationId();

	const languageSetting = getApplicationLanguage();
	const { isLoading: isLoadingCountries, data: countries = [] } = useFetchClcCountriesQuery();

	const sortedCountries = countries
		.concat()
		.sort((a, b) => alphabeticalSort(a.names[languageSetting], b.names[languageSetting]));

	const countryCodeFormValues = convertCountriesToFormValues(
		countries.filter((c) => c.partOfEes || c.alpha2Code === "GB"),
	);

	const [postClcCompanyRegistration, { isLoading: isPostingClcCompanyRegistration }] =
		usePostClcCompanyRegistrationMutation();

	const [postClcBeneficialOwnership, { isLoading: isPostingClcBeneficialOwnership }] =
		usePostClcBeneficialOwnershipMutation();

	const [postClcAmlAnswers] = usePostClcAmlAnswersMutation();
	const [postClcBeneficialOwnerAnswers] = usePostClcBeneficialOwnerAnswersMutation();
	const [setupClcAgreement, { isLoading: isPostingClcAgreement }] = useSetupClcAgreementMutation();
	const [postClcRepresentatives, { isLoading: isPostingClcRepresentatives }] =
		usePostClcRepresentativesMutation();
	const [configureClcCard, { isLoading: isPostingClcCardConfig }] = useConfigureClcCardMutation();
	const [selectClcAddon, { isLoading: isSelectingClcAddon }] = useSelectClcAddonMutation();
	const [removeClcAddons, { isLoading: isRemovingClcAddons }] = useRemoveClcAddonsMutation();
	const [confirmClcProductConfig, { isLoading: isConfirmingProducts }] =
		useConfirmClcProductConfigMutation();
	const [postClcApplicationQuestions, { isLoading: isPostingApplicationQuestions }] =
		usePostClcApplicationQuestionsMutation();

	const submitClcApplicationQuestions = async (questions: Array<AnsweredQuestion>) => {
		await postClcApplicationQuestions({
			applicationId,
			questions,
		}).unwrap();
	};

	const submitClcCompanyRegistration = async (values: ClcCompanyRegistrationForm) => {
		const companyInfo = omit(values, "confirmEmail");
		await postClcCompanyRegistration({
			applicationId,
			companyInfo,
		}).unwrap();
	};

	const [postClcCompanyShares, { isLoading: isPostingClcCompanyShares }] =
		usePostClcCompanySharesMutation();

	const submitClcCompanyShares = async (values: Partial<ClcCompanySharesForm>) => {
		const companyShares = omit(values, "numberOfShares");
		await postClcCompanyShares({
			applicationId,
			shares: companyShares as ClcCompanyShares, // todo: maybe make form fields initialvalues null instead of assertion?
		}).unwrap();
	};

	const submitClcBeneficialOwnership = async (beneficialOwners: ClcBeneficialOwnership) => {
		await postClcBeneficialOwnership({
			applicationId,
			beneficialOwners,
		}).unwrap();
	};

	const submitClcAmlAnswers = async (productId: string, questions: AnsweredQuestion[]) => {
		await postClcAmlAnswers({
			applicationId,
			questions,
			productId,
		}).unwrap();
	};

	const submitClcBeneficialOwnerAnswers = async (answers: ClcBeneficialOwnerAnswerPostReq) => {
		await postClcBeneficialOwnerAnswers({
			applicationId,
			...answers,
		}).unwrap();
	};

	const submitClcRepresentatives = async (representatives: ClcCompanyStructureInput) => {
		await postClcRepresentatives({
			applicationId,
			representatives,
		}).unwrap();
	};

	const configureClcDebitCard = async (cardConfig: ClcDebitCardConfigForm) => {
		const productId = cardConfig.productId || getUuid();
		if (!cardConfig.productId) {
			await selectClcAddon({
				addonConfig: { productDefinitionId: "DEBITCARD", productId },
				applicationId,
			}).unwrap();
		}
		await configureClcCard({
			cardConfig: {
				...cardConfig,
				productId: cardConfig.productId || productId,
			},
			applicationId,
		}).unwrap();
	};

	const confirmProductConfig = async () => {
		// should not occur:
		const unconfiguredAddons =
			cards?.debitCards.cards
				.filter((c) => c.isAddon === true)
				.filter((c) => c.isConfigured === false)
				.map((c) => c.productId) || [];
		if (unconfiguredAddons?.length > 0) {
			await removeClcAddons({
				applicationId,
				addons: unconfiguredAddons,
			}).unwrap();
		}

		await confirmClcProductConfig(applicationId).unwrap();
	};

	const countryCodes: Array<FieldOption> = Array.from(countryCodeFormValues, ([value, label]) => ({
		label,
		value,
	}));

	const { data: clcApplications, isLoading: isLoadingClcApplications } =
		useFetchClcApplicationsQuery();

	const { data: beneficialOwnerQuestions, isLoading: isLoadingBeneficialOwnerQuestions } =
		useFetchClcBeneficialOwnerQuestionsQuery(applicationId);

	const { data: allSniCodes, isLoading: isLoadingSniCodes } = useFetchClcSniCodesQuery();

	const { data: controlTypeMap, isLoading: isLoadingControlTypes } = useFetchClcControlTypesQuery();

	const { data: clcApplicationDetails, isLoading: isLoadingClcApplicationDetails } =
		useFetchClcApplicationDetailsQuery(applicationId);

	const { data: amlQuestions, isLoading: isLoadingAmlQuestions } =
		useFetchClcAmlQuestionsQuery(applicationId);

	const { data: roles, isLoading: isLoadingRoles } = useFetchClcRolesQuery();

	const { data: cards, isLoading: isLoadingCards } = useFetchClcCardsQuery(applicationId);

	const { data: selectedPackage, isLoading: isLoadingSelectedPackage } =
		useFetchClcSelectedPackageQuery(applicationId);

	const { data: signatoryCombinations, isLoading: isLoadingSignatoryCombinations } =
		useFetchClcSignatoryCombinationsQuery();

	const { data: clcApplicationQuestions = [] } =
		useFetchClcApplicationQuestionsQuery(applicationId);

	const isLoading = isSome(
		isLoadingBeneficialOwnerQuestions,
		isLoadingClcApplications,
		isLoadingClcApplicationDetails,
		isLoadingCountries,
		isLoadingSniCodes,
		isLoadingControlTypes,
		isLoadingAmlQuestions,
		isLoadingRoles,
		isLoadingSignatoryCombinations,
		isLoadingCards,
		isLoadingSelectedPackage,
	);

	return {
		applicationId,
		clcApplications,
		clcApplicationDetails,
		allSniCodes,
		countryCodes,
		beneficialOwnerQuestions,
		submitClcCompanyRegistration,
		isPostingClcCompanyRegistration,
		submitClcCompanyShares,
		submitClcBeneficialOwnership,
		setupClcAgreement,
		submitClcRepresentatives,
		isPostingClcCompanyShares,
		isPostingClcBeneficialOwnership,
		isPostingClcRepresentatives,
		isPostingClcCardConfig,
		controlTypeMap,
		roles,
		signatoryCombinations,
		isLoading,
		amlQuestions,
		submitClcAmlAnswers,
		submitClcBeneficialOwnerAnswers,
		isPostingClcAgreement,
		sortedCountries,
		cards,
		selectedPackage,
		configureClcDebitCard,
		isSelectingClcAddon,
		selectClcAddon,
		isRemovingClcAddons,
		isConfirmingProducts,
		confirmProductConfig,
		removeClcAddons,
		clcApplicationQuestions,
		submitClcApplicationQuestions,
		isPostingApplicationQuestions,
	};
};
